'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Granularny banner zgody na cookies — zgodny z RODO/ePrivacy.
 *
 * 3 kategorie:
 *   - necessary  → zawsze aktywne (sesja, reCAPTCHA, zapamiętanie preferencji)
 *   - analytics  → opt-in (Google Analytics)
 *   - marketing  → opt-in (obecnie nieużywane, gotowe do przyszłego użycia)
 *
 * Preferencje zapisywane w localStorage jako JSON pod kluczem `cookieConsentSettings`.
 * Komponenty trackingowe (np. GoogleAnalytics) nasłuchują eventu `cookieConsentChanged`
 * z payloadem `{ analytics: boolean, marketing: boolean, necessary: true }`.
 *
 * Zgodność prawna:
 *   - opt-in (TSUE Planet49 C-673/17)
 *   - przyciski "Akceptuj wszystkie" i "Tylko niezbędne" są równorzędne
 *   - możliwość wycofania zgody w każdej chwili (przycisk w stopce — TODO przyszłość)
 *   - granularność każdej kategorii (UODO)
 */

const STORAGE_KEY = 'cookieConsentSettings';
const LEGACY_KEY = 'cookieConsent'; // stary klucz (jeden boolean)
const CONSENT_VERSION = 2;

export interface CookieConsentSettings {
  version: number;
  necessary: true; // zawsze true — niezbędne do działania strony
  analytics: boolean;
  marketing: boolean;
  decidedAt: string; // ISO timestamp
}

const DEFAULT_DENIED: CookieConsentSettings = {
  version: CONSENT_VERSION,
  necessary: true,
  analytics: false,
  marketing: false,
  decidedAt: '',
};

function readSettings(): CookieConsentSettings | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentSettings;
    if (parsed && parsed.version === CONSENT_VERSION && typeof parsed.decidedAt === 'string' && parsed.decidedAt) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function writeSettings(settings: CookieConsentSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    // Czyszczenie legacy klucza (z poprzedniej wersji bannera)
    localStorage.removeItem(LEGACY_KEY);
  } catch {
    /* private browsing */
  }
}

function emitConsentChange(settings: CookieConsentSettings) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('cookieConsentChanged', {
      detail: {
        analytics: settings.analytics,
        marketing: settings.marketing,
        necessary: settings.necessary,
      },
    })
  );
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Wybory w panelu szczegółowym (kontrolowane checkboxy)
  const [analyticsChoice, setAnalyticsChoice] = useState(false);
  const [marketingChoice, setMarketingChoice] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const existing = readSettings();
    if (existing) {
      // Powiadom słuchaczy o aktualnym stanie (dla GoogleAnalytics po przeładowaniu)
      emitConsentChange(existing);
      setAnalyticsChoice(existing.analytics);
      setMarketingChoice(existing.marketing);
      return;
    }

    // Pierwsza wizyta lub stare ustawienia — pokaż banner
    const timer = setTimeout(() => setShowBanner(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const saveAndClose = (settings: CookieConsentSettings) => {
    writeSettings(settings);
    emitConsentChange(settings);
    setShowBanner(false);
    setShowDetails(false);
  };

  const acceptAll = () => {
    saveAndClose({
      ...DEFAULT_DENIED,
      analytics: true,
      marketing: true,
      decidedAt: new Date().toISOString(),
    });
  };

  const rejectAll = () => {
    saveAndClose({
      ...DEFAULT_DENIED,
      analytics: false,
      marketing: false,
      decidedAt: new Date().toISOString(),
    });
  };

  const saveSelection = () => {
    saveAndClose({
      ...DEFAULT_DENIED,
      analytics: analyticsChoice,
      marketing: marketingChoice,
      decidedAt: new Date().toISOString(),
    });
  };

  const openSettings = () => {
    const existing = readSettings();
    setAnalyticsChoice(existing?.analytics ?? false);
    setMarketingChoice(existing?.marketing ?? false);
    setShowDetails(true);
  };

  return (
    <>
      {/* Główny banner — pokazywany tylko gdy brak zapisanych preferencji */}
      <AnimatePresence>
        {showBanner && (
          <motion.div
            role="dialog"
            aria-labelledby="cookie-banner-title"
            aria-describedby="cookie-banner-desc"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed bottom-0 left-0 right-0 z-50 bg-white shadow-2xl border-t-4 border-[rgb(0,65,125)]"
          >
            <div className="max-w-6xl mx-auto p-5 md:p-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <h2 id="cookie-banner-title" className="text-lg font-bold text-[rgb(0,65,125)] mb-2">
                    Szanujemy Twoją prywatność
                  </h2>
                  <p id="cookie-banner-desc" className="text-sm text-gray-700 leading-relaxed">
                    Ta strona używa plików cookies. Pliki niezbędne (np. obsługa formularzy i zabezpieczenie reCAPTCHA) są aktywne zawsze.{' '}
                    <strong>Cookies analityczne i marketingowe wymagają Twojej zgody.</strong> Zgodę możesz wyrazić, odrzucić lub zmienić jej zakres w dowolnym momencie. Szczegóły w{' '}
                    <a href="/rodo" className="underline text-[rgb(0,65,125)] hover:text-[rgb(0,65,125)]/80">
                      Polityce Prywatności
                    </a>
                    .
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 md:min-w-fit">
                  <button
                    onClick={rejectAll}
                    className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-md transition-colors border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[rgb(0,65,125)]"
                  >
                    Tylko niezbędne
                  </button>
                  <button
                    onClick={openSettings}
                    className="px-4 py-2 text-sm bg-white hover:bg-gray-50 text-[rgb(0,65,125)] rounded-md transition-colors border border-[rgb(0,65,125)] focus:outline-none focus:ring-2 focus:ring-[rgb(0,65,125)]"
                  >
                    Ustaw szczegółowo
                  </button>
                  <button
                    onClick={acceptAll}
                    className="px-4 py-2 text-sm bg-[rgb(0,65,125)] hover:bg-[rgb(0,65,125)]/90 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(0,65,125)] focus:ring-offset-2 font-medium"
                  >
                    Akceptuj wszystkie
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal szczegółowych ustawień */}
      <AnimatePresence>
        {showDetails && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-settings-title"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowDetails(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
                <h2 id="cookie-settings-title" className="text-xl font-bold text-[rgb(0,65,125)]">
                  Ustawienia plików cookies
                </h2>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[rgb(0,65,125)] rounded"
                  aria-label="Zamknij ustawienia"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4 text-gray-700">
                <p className="text-sm">
                  Wybierz, na jakie kategorie plików cookies wyrażasz zgodę. Możesz zmienić te ustawienia w dowolnym momencie. Pełna informacja o przetwarzaniu danych — w{' '}
                  <a href="/rodo" className="underline text-[rgb(0,65,125)]" target="_blank" rel="noopener noreferrer">
                    Polityce Prywatności
                  </a>
                  .
                </p>

                {/* Kategoria: niezbędne */}
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Cookies niezbędne</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Wymagane do działania strony — obsługa sesji, zapamiętanie tych ustawień, zabezpieczenie formularzy (Google reCAPTCHA). Nie można ich wyłączyć.
                      </p>
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs px-2 py-1 bg-[rgb(0,65,125)] text-white rounded">Zawsze aktywne</span>
                    </div>
                  </div>
                </div>

                {/* Kategoria: analityczne */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Cookies analityczne</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Google Analytics — anonimowe statystyki ruchu pomagające nam ulepszać stronę. Adres IP jest anonimizowany.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={analyticsChoice}
                        onChange={(e) => setAnalyticsChoice(e.target.checked)}
                        aria-label="Włącz cookies analityczne"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[rgb(0,65,125)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(0,65,125)]"></div>
                    </label>
                  </div>
                </div>

                {/* Kategoria: marketingowe */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Cookies marketingowe</h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Obecnie nie używamy cookies marketingowych. Ta opcja jest przygotowana na przyszłość — Twoja zgoda zostanie zapamiętana.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={marketingChoice}
                        onChange={(e) => setMarketingChoice(e.target.checked)}
                        aria-label="Włącz cookies marketingowe"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[rgb(0,65,125)] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[rgb(0,65,125)]"></div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-2 justify-end">
                <button
                  onClick={rejectAll}
                  className="px-4 py-2 bg-white hover:bg-gray-100 text-gray-800 rounded-md border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(0,65,125)]"
                >
                  Tylko niezbędne
                </button>
                <button
                  onClick={saveSelection}
                  className="px-4 py-2 bg-white hover:bg-gray-100 text-[rgb(0,65,125)] rounded-md border border-[rgb(0,65,125)] transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(0,65,125)]"
                >
                  Zapisz wybór
                </button>
                <button
                  onClick={acceptAll}
                  className="px-4 py-2 bg-[rgb(0,65,125)] hover:bg-[rgb(0,65,125)]/90 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-[rgb(0,65,125)] focus:ring-offset-2 font-medium"
                >
                  Akceptuj wszystkie
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;
